import { makeAutoObservable, runInAction } from "mobx"
import {
    clearEmbeddedBrowserData,
    dismissEmbeddedBrowserCertificateError,
    getEmbeddedBrowserStatus,
    proceedEmbeddedBrowserCertificateError,
    subscribeEmbeddedBrowserEvents,
    type EmbeddedBrowserShortcut,
} from "../electronAPIs/browser"
import type { ClientToolContext } from "../nest/clientTools"
import type { BrowserScreenshotObservation } from "./observation"
import { isAllowedBrowserUrl } from "./browserUrl"
import type { BrowserElementFingerprint } from "./scripts"
import { embeddedBrowserSurfaceStore } from "./surfaceStore"
import { EmbeddedBrowserTab, type BrowserSurface } from "./tab"

const MAX_TABS = 8
const MAX_SCREENSHOTS_PER_TAB = 12

export type BrowserUserActionFailureReason = "tab-closed" | "browser-reset" | "browser-stopped" | "browser-disposed"

export type BrowserUserActionRequest = {
    id: string
    instruction: string
    sessionId: string
    tabId: string
    permissionMode: ClientToolContext["permissionMode"]
    status: "waiting" | "delivering" | "delivery-failed"
    outcome?: "completed" | "unable"
    failureReason?: BrowserUserActionFailureReason
    userNotes?: string
    error?: string
}

export type BrowserUserActionRegistration = {
    waiting: true
    requestId: string
    tabId: string
    instruction: string
}

export type BrowserUserActionOutcomeHandler = (
    request: BrowserUserActionRequest,
    outcome: "completed" | "unable",
    failureReason?: BrowserUserActionFailureReason
) => Promise<void>

export class EmbeddedBrowserStore {
    tabs: EmbeddedBrowserTab[] = []
    activeTabId: string | null = null
    partition: string | null = null
    notice: string | null = null
    addressFocusGeneration = 0
    readonly pendingUserActions = new Map<string, BrowserUserActionRequest>()
    readonly fingerprintRegistry = new Map<string, Map<string, BrowserElementFingerprint>>()
    readonly screenshotObservations = new Map<string, BrowserScreenshotObservation>()
    readonly activeAgentActivities = new Map<string, { name: string; context: ClientToolContext }>()
    readonly pausedAgentSessions = new Set<string>()
    readonly manualActionTabIds = new Map<string, string>()

    private generation = 0
    private initializePromise: Promise<void> | null = null
    private unsubscribeEvents: (() => void) | null = null
    private readonly actionQueues = new Map<string, Promise<void>>()
    private readonly agentActiveTabIds = new Map<string, string>()
    private globallyPaused = false
    private agentStoppedGeneration: number | null = null
    private surfaceInteractive = false
    private userActionOutcomeHandler: BrowserUserActionOutcomeHandler | null = null

    constructor() {
        makeAutoObservable<EmbeddedBrowserStore, "actionQueues" | "agentActiveTabIds" | "userActionOutcomeHandler">(this, {
            fingerprintRegistry: false,
            screenshotObservations: false,
            actionQueues: false,
            agentActiveTabIds: false,
            userActionOutcomeHandler: false,
        })
        this.createTab("about:blank", { agentClaimable: true })
    }

    get activeTab(): EmbeddedBrowserTab | null {
        return this.tabs.find((tab) => tab.id === this.activeTabId) ?? null
    }

    get visibleTabAgentAction(): string | null {
        const ownerSessionId = this.activeTab?.ownerSessionId
        return ownerSessionId ? (this.activeAgentActivities.get(ownerSessionId)?.name ?? null) : null
    }

    get activeAgentCount(): number {
        return this.activeAgentActivities.size
    }

    get globalAgentPaused(): boolean {
        return this.globallyPaused
    }

    get visibleTabAgentPaused(): boolean {
        const ownerSessionId = this.activeTab?.ownerSessionId
        return ownerSessionId ? this.pausedAgentSessions.has(ownerSessionId) || this.hasPendingUserAction(ownerSessionId) : false
    }

    get visibleTabUserAction(): BrowserUserActionRequest | null {
        return this.activeTabId ? (this.pendingUserActions.get(this.activeTabId) ?? null) : null
    }

    hasPendingUserAction(sessionId: string): boolean {
        return [...this.pendingUserActions.values()].some((request) => request.sessionId === sessionId)
    }

    get visibleTabManualActionRequired(): boolean {
        const activeTab = this.activeTab
        return activeTab?.ownerSessionId ? this.manualActionTabIds.get(activeTab.ownerSessionId) === activeTab.id : false
    }

    get pausedAgentCount(): number {
        return this.pausedAgentSessions.size
    }

    get agentPaused(): boolean {
        return this.globallyPaused || this.visibleTabAgentPaused
    }

    async initialize(): Promise<void> {
        if (this.tabs.length === 0) this.createTab("about:blank", { agentClaimable: true })
        if (this.partition) return
        if (this.initializePromise) return this.initializePromise
        this.initializePromise = (async () => {
            const status = await getEmbeddedBrowserStatus()
            if (!status) throw new Error("browser_unavailable")
            runInAction(() => {
                this.partition = status.partition
            })
            this.unsubscribeEvents ??= subscribeEmbeddedBrowserEvents((event) => {
                if (event.type === "partition-rotated") {
                    if (this.partition === event.partition) return
                    if (this.partition) this.stopSession()
                    else this.initializePromise = null
                    this.setNotice("Browser profile was replaced with newly imported website sessions.")
                    return
                }
                if (event.type === "profile-cleared") {
                    this.reset()
                    this.setNotice("Browser data was cleared.")
                    return
                }
                const sourceGuestId = event.type === "certificate-error" ? event.warning.guestId : event.guestId
                const sourceTab = this.tabForGuest(sourceGuestId)
                if (!sourceTab) return
                if (event.type === "certificate-error") {
                    sourceTab.showCertificateError(event.warning)
                    this.notice = null
                    this.selectTab(sourceTab.id)
                    embeddedBrowserSurfaceStore.reveal()
                } else if (event.type === "open-tab") {
                    try {
                        const tab = this.createTab(event.url, {
                            ownerSessionId: sourceTab.ownerSessionId,
                            activate: this.activeTabId === sourceTab.id,
                        })
                        if (sourceTab.ownerSessionId) this.agentActiveTabIds.set(sourceTab.ownerSessionId, tab.id)
                    } catch {
                        this.setNotice("Browser can keep up to eight tabs open.")
                    }
                } else if (event.type === "download-blocked") {
                    this.setNotice("Downloads are blocked in the embedded Browser.")
                } else if (event.type === "navigation-blocked") {
                    this.setNotice("Browser blocked an unsafe navigation.")
                } else if (event.type === "shortcut") {
                    this.handleShortcut(sourceTab.id, event.command)
                }
            })
        })()
        try {
            await this.initializePromise
        } catch (error) {
            this.initializePromise = null
            throw error
        }
    }

    createTab(
        url = "about:blank",
        options: {
            ownerSessionId?: string | null
            activate?: boolean
            agentClaimable?: boolean
        } = {}
    ): EmbeddedBrowserTab {
        if (this.tabs.length >= MAX_TABS) throw new Error("browser_tab_limit")
        if (!isAllowedBrowserUrl(url)) throw new Error("browser_unsafe_url")
        const tabId = crypto.randomUUID()
        const tab = new EmbeddedBrowserTab(
            tabId,
            url,
            this.generation,
            () => {
                this.fingerprintRegistry.delete(tabId)
                this.clearScreenshots(tabId)
            },
            {
                ownerSessionId: options.ownerSessionId,
                agentClaimable: options.agentClaimable,
            }
        )
        this.tabs.push(tab)
        if (options.activate !== false || this.activeTabId === null) this.activeTabId = tab.id
        this.syncActiveTabs()
        return tab
    }

    ensureAgentTab(context: ClientToolContext): EmbeddedBrowserTab {
        const existing = this.agentTabs(context)
        const selectedId = this.agentActiveTabIds.get(context.sessionId)
        const selected = existing.find((tab) => tab.id === selectedId) ?? existing[0]
        if (selected) {
            this.agentActiveTabIds.set(context.sessionId, selected.id)
            return selected
        }

        const claimable = this.tabs.find(
            (tab) =>
                tab.agentClaimable && tab.ownerSessionId === null && tab.url === "about:blank" && tab.navigationGeneration === 0 && tab.history.length === 1
        )
        if (claimable) {
            claimable.claimForAgent(context.sessionId)
            this.agentActiveTabIds.set(context.sessionId, claimable.id)
            return claimable
        }

        return this.createAgentTab(context)
    }

    agentTabs(context: ClientToolContext): EmbeddedBrowserTab[] {
        return this.tabs.filter((tab) => tab.ownerSessionId === context.sessionId)
    }

    activeAgentTabId(context: ClientToolContext): string | null {
        const selectedId = this.agentActiveTabIds.get(context.sessionId)
        if (selectedId && this.tab(selectedId)?.ownerSessionId === context.sessionId) return selectedId
        const first = this.agentTabs(context)[0]
        if (!first) return null
        this.agentActiveTabIds.set(context.sessionId, first.id)
        return first.id
    }

    requireAgentTab(context: ClientToolContext, tabId?: string): EmbeddedBrowserTab {
        const targetId = tabId ?? this.activeAgentTabId(context)
        if (!targetId) throw new Error("browser_tab_not_found")
        const tab = this.tab(targetId)
        if (!tab) throw new Error("browser_tab_not_found")
        if (tab.ownerSessionId !== context.sessionId) throw new Error("browser_tab_not_owned")
        return tab
    }

    selectAgentTab(context: ClientToolContext, tabId: string): EmbeddedBrowserTab {
        const tab = this.requireAgentTab(context, tabId)
        this.agentActiveTabIds.set(context.sessionId, tab.id)
        return tab
    }

    createAgentTab(context: ClientToolContext, url = "about:blank"): EmbeddedBrowserTab {
        const tab = this.createTab(url, {
            ownerSessionId: context.sessionId,
            activate: false,
        })
        this.agentActiveTabIds.set(context.sessionId, tab.id)
        return tab
    }

    selectTab(tabId: string): EmbeddedBrowserTab {
        const tab = this.requireTab(tabId)
        this.activeTabId = tab.id
        this.syncActiveTabs()
        return tab
    }

    closeTab(tabId: string, initiatedByAgent = false): void {
        const tab = this.tab(tabId)
        if (!tab) return
        const ownerSessionId = tab.ownerSessionId
        const request = this.pendingUserActions.get(tabId)
        if (request) {
            void this.deliverUserActionOutcome({ tabId, requestId: request.id, requestedOutcome: "unable", requestedFailureReason: "tab-closed" })
            this.setNotice("The Browser tab was closed. The agent will ask whether you want to try again.")
        }
        tab.dispose()
        this.tabs = this.tabs.filter((candidate) => candidate.id !== tabId)
        this.fingerprintRegistry.delete(tabId)
        this.clearScreenshots(tabId)
        if (this.activeTabId === tabId) this.activeTabId = this.tabs[0]?.id ?? null
        if (ownerSessionId && this.manualActionTabIds.get(ownerSessionId) === tabId) {
            this.manualActionTabIds.delete(ownerSessionId)
            this.pausedAgentSessions.delete(ownerSessionId)
        }
        if (ownerSessionId && this.agentActiveTabIds.get(ownerSessionId) === tabId) {
            const nextOwnedTab = this.tabs.find((candidate) => candidate.ownerSessionId === ownerSessionId)
            if (nextOwnedTab) this.agentActiveTabIds.set(ownerSessionId, nextOwnedTab.id)
            else {
                this.agentActiveTabIds.delete(ownerSessionId)
                this.pausedAgentSessions.delete(ownerSessionId)
                this.manualActionTabIds.delete(ownerSessionId)
            }
        }
        this.syncActiveTabs()
        if (this.tabs.length === 0) this.stopSession(initiatedByAgent)
    }

    reset(): void {
        this.failUserActions("browser-reset")
        this.agentStoppedGeneration = null
        this.generation += 1
        for (const tab of this.tabs) tab.dispose()
        this.fingerprintRegistry.clear()
        this.screenshotObservations.clear()
        this.activeAgentActivities.clear()
        this.pausedAgentSessions.clear()
        this.manualActionTabIds.clear()
        this.agentActiveTabIds.clear()
        this.tabs = []
        this.activeTabId = null
        this.globallyPaused = true
        this.createTab("about:blank", { agentClaimable: true })
    }

    stopSession(initiatedByAgent = false): void {
        this.failUserActions("browser-stopped")
        this.agentStoppedGeneration = initiatedByAgent ? this.generation : null
        this.generation += 1
        for (const tab of this.tabs) tab.dispose()
        this.fingerprintRegistry.clear()
        this.screenshotObservations.clear()
        this.activeAgentActivities.clear()
        this.pausedAgentSessions.clear()
        this.manualActionTabIds.clear()
        this.agentActiveTabIds.clear()
        this.tabs = []
        this.activeTabId = null
        this.globallyPaused = true
        this.notice = null
        this.partition = null
        this.initializePromise = null
        this.surfaceInteractive = false
        embeddedBrowserSurfaceStore.stop()
    }

    dispose(): void {
        this.failUserActions("browser-disposed")
        this.unsubscribeEvents?.()
        this.unsubscribeEvents = null
        for (const tab of this.tabs) tab.dispose()
        this.fingerprintRegistry.clear()
        this.screenshotObservations.clear()
    }

    setNotice(notice: string | null): void {
        this.notice = notice
    }

    setAgentPaused(paused: boolean): void {
        const ownerSessionId = this.activeTab?.ownerSessionId
        if (ownerSessionId) {
            if (paused) this.pausedAgentSessions.add(ownerSessionId)
            else {
                this.pausedAgentSessions.delete(ownerSessionId)
                this.manualActionTabIds.delete(ownerSessionId)
            }
        } else if (paused) {
            this.setNotice("This Browser tab is not controlled by an agent.")
            return
        }
        this.setNotice(paused ? "You have control. Agent actions for this Browser tab are paused." : "Agent Browser control resumed for this tab.")
    }

    resumeGlobalAgentControl(): void {
        this.globallyPaused = false
        this.setNotice("The Browser reset pause was cleared. Any tab-level pauses remain in place.")
    }

    isAgentPaused(context: ClientToolContext): boolean {
        return this.globallyPaused || this.pausedAgentSessions.has(context.sessionId) || this.hasPendingUserAction(context.sessionId)
    }

    requireManualAction(context: ClientToolContext): void {
        this.pausedAgentSessions.add(context.sessionId)
        const tabId = this.activeAgentTabId(context)
        if (tabId) this.manualActionTabIds.set(context.sessionId, tabId)
    }

    setUserActionOutcomeHandler(handler: BrowserUserActionOutcomeHandler): () => void {
        this.userActionOutcomeHandler = handler
        return () => {
            if (this.userActionOutcomeHandler === handler) this.userActionOutcomeHandler = null
        }
    }

    requestUserAction(context: ClientToolContext, instruction: string, tabId?: string): BrowserUserActionRegistration {
        if (!embeddedBrowserSurfaceStore.isRunning) throw new Error("browser_start_required")
        const tab = this.requireAgentTab(context, tabId)
        if (this.pendingUserActions.has(tab.id)) throw new Error("browser_user_action_pending")
        const preserveVisibleRequest = embeddedBrowserSurfaceStore.isVisible && this.visibleTabUserAction !== null
        const request: BrowserUserActionRequest = {
            id: crypto.randomUUID(),
            instruction,
            sessionId: context.sessionId,
            tabId: tab.id,
            permissionMode: context.permissionMode,
            status: "waiting",
        }
        this.pendingUserActions.set(tab.id, request)
        this.pausedAgentSessions.add(context.sessionId)
        this.notice = null
        if (!preserveVisibleRequest) this.selectTab(tab.id)
        embeddedBrowserSurfaceStore.reveal()
        return { waiting: true, requestId: request.id, tabId: tab.id, instruction }
    }

    async completeUserAction({
        tabId,
        requestId,
        completed,
        userNotes,
    }: {
        tabId: string
        requestId: string
        completed: boolean
        userNotes?: string
    }): Promise<void> {
        await this.deliverUserActionOutcome({ tabId, requestId, requestedOutcome: completed ? "completed" : "unable", requestedUserNotes: userNotes })
    }

    private failUserActions(failureReason: BrowserUserActionFailureReason): void {
        for (const request of this.pendingUserActions.values()) {
            void this.deliverUserActionOutcome({
                tabId: request.tabId,
                requestId: request.id,
                requestedOutcome: "unable",
                requestedFailureReason: failureReason,
            })
        }
    }

    private async deliverUserActionOutcome({
        tabId,
        requestId,
        requestedOutcome,
        requestedFailureReason,
        requestedUserNotes,
    }: {
        tabId: string
        requestId: string
        requestedOutcome: "completed" | "unable"
        requestedFailureReason?: BrowserUserActionFailureReason
        requestedUserNotes?: string
    }): Promise<void> {
        const request = this.pendingUserActions.get(tabId)
        if (!request || request.id !== requestId || request.status === "delivering") return
        const outcome = request.outcome ?? requestedOutcome
        const failureReason = request.failureReason ?? requestedFailureReason
        const normalizedUserNotes = requestedUserNotes?.trim()
        const userNotes = request.userNotes ?? (normalizedUserNotes || undefined)
        const deliveryRequest: BrowserUserActionRequest = {
            ...request,
            status: "delivering",
            outcome,
            failureReason,
            userNotes,
            error: undefined,
        }
        this.pendingUserActions.set(tabId, deliveryRequest)
        const handler = this.userActionOutcomeHandler
        if (!handler) {
            this.pendingUserActions.set(tabId, {
                ...deliveryRequest,
                status: "delivery-failed",
                error: "Browser could not reach the originating task.",
            })
            return
        }
        try {
            await handler(deliveryRequest, outcome, failureReason)
            runInAction(() => {
                if (this.pendingUserActions.get(tabId)?.id !== request.id) return
                this.pendingUserActions.delete(tabId)
                if (!this.hasPendingUserAction(request.sessionId)) {
                    this.pausedAgentSessions.delete(request.sessionId)
                    this.manualActionTabIds.delete(request.sessionId)
                }
                if (failureReason === "tab-closed") {
                    this.notice = "The Browser tab was closed. A new agent turn will ask whether you want to try again."
                } else if (failureReason) {
                    this.notice = "Browser closed before the requested step finished. A new agent turn was started with that result."
                } else {
                    this.notice =
                        outcome === "completed"
                            ? "Thanks — a new agent turn was started with your result."
                            : "A new agent turn was started with the result that you could not complete the request."
                }
                if (this.activeTabId === tabId && this.pendingUserActions.size === 0) embeddedBrowserSurfaceStore.hide()
            })
        } catch (error) {
            runInAction(() => {
                if (this.pendingUserActions.get(tabId)?.id !== request.id) return
                this.pendingUserActions.set(tabId, {
                    ...deliveryRequest,
                    status: "delivery-failed",
                    error: error instanceof Error ? error.message : "Browser could not start the next agent turn.",
                })
                this.notice = "The Browser result was not delivered. It is still pending and can be retried."
            })
        }
    }

    async clearData(): Promise<void> {
        if (!(await clearEmbeddedBrowserData())) throw new Error("browser_clear_failed")
    }

    async navigate(tabId: string, url: string): Promise<void> {
        if (!isAllowedBrowserUrl(url)) throw new Error("browser_unsafe_url")
        const surface = await this.surface(tabId)
        await surface.loadURL(url)
    }

    async proceedCertificateError(tabId: string): Promise<void> {
        const tab = this.requireTab(tabId)
        const warning = tab.certificateError
        if (!warning) throw new Error("browser_certificate_challenge_missing")
        const approval = await proceedEmbeddedBrowserCertificateError(warning.challengeId)
        if (!approval || approval.url !== warning.url || tab.certificateError?.challengeId !== warning.challengeId)
            throw new Error("browser_certificate_challenge_expired")
        tab.clearCertificateError(warning.challengeId)
    }

    async leaveCertificateError(tabId: string): Promise<void> {
        const tab = this.requireTab(tabId)
        const warning = tab.certificateError
        if (!warning) return
        await dismissEmbeddedBrowserCertificateError(warning.challengeId)
        tab.clearCertificateError(warning.challengeId)
        const destination = tab.url !== warning.url ? tab.url : "about:blank"
        await this.navigate(tab.id, destination)
    }

    async waitForNavigation(tabId: string, previousGeneration: number, required = false): Promise<boolean> {
        const shortDeadline = Date.now() + 250
        const longDeadline = Date.now() + 10_000
        let sawLoading = false
        let tab = this.tab(tabId)
        while (tab && tab.navigationGeneration === previousGeneration && Date.now() < (required || sawLoading ? longDeadline : shortDeadline)) {
            if (tab.crashed) throw new Error("browser_tab_crashed")
            if (tab.errorCode) throw new Error(`browser_${tab.errorCode}`)
            if (tab.loading) sawLoading = true
            await new Promise<void>((resolve) => window.setTimeout(resolve, 25))
            tab = this.tab(tabId)
        }
        if (!tab) throw new Error("browser_tab_not_found")
        if (tab.navigationGeneration === previousGeneration) {
            if (required || sawLoading) throw new Error("browser_navigation_timeout")
            return false
        }
        const loadDeadline = Date.now() + 10_000
        while (tab.loading && Date.now() < loadDeadline) {
            await new Promise<void>((resolve) => window.setTimeout(resolve, 25))
            tab = this.tab(tabId)
            if (!tab) throw new Error("browser_tab_not_found")
        }
        if (tab.loading) throw new Error("browser_navigation_timeout")
        if (tab.crashed) throw new Error("browser_tab_crashed")
        if (tab.errorCode) throw new Error(`browser_${tab.errorCode}`)
        return true
    }

    async surface(tabId: string): Promise<BrowserSurface> {
        if (!embeddedBrowserSurfaceStore.isRunning) throw new Error("browser_start_required")
        await this.initialize()
        const startedAt = Date.now()
        while (Date.now() - startedAt < 5_000) {
            const surface = this.tab(tabId)?.surface()
            if (surface) return surface
            await new Promise<void>((resolve) => window.setTimeout(resolve, 25))
        }
        this.setNotice("Browser could not attach to this tab.")
        throw new Error("browser_surface_unavailable")
    }

    setFingerprints(tabId: string, fingerprints: Map<string, BrowserElementFingerprint>): void {
        this.fingerprintRegistry.set(tabId, fingerprints)
    }

    fingerprint(tabId: string, ref: string): BrowserElementFingerprint {
        const fingerprint = this.fingerprintRegistry.get(tabId)?.get(ref)
        if (!fingerprint) throw new Error("browser_stale_ref")
        return fingerprint
    }

    rememberScreenshot(observation: BrowserScreenshotObservation): void {
        this.screenshotObservations.set(observation.id, observation)
        const tabObservationIds = [...this.screenshotObservations].filter(([, candidate]) => candidate.tabId === observation.tabId).map(([id]) => id)
        while (tabObservationIds.length > MAX_SCREENSHOTS_PER_TAB) {
            const oldest = tabObservationIds.shift()
            if (!oldest) break
            this.screenshotObservations.delete(oldest)
        }
    }

    screenshot(id: string): BrowserScreenshotObservation {
        const observation = this.screenshotObservations.get(id)
        if (!observation) throw new Error("browser_stale_screenshot")
        return observation
    }

    requireTab(tabId?: string): EmbeddedBrowserTab {
        const tab = tabId ? this.tab(tabId) : this.activeTab
        if (!tab) throw new Error("browser_tab_not_found")
        return tab
    }

    setSurfaceInteractive(interactive: boolean): void {
        this.surfaceInteractive = interactive
        this.syncActiveTabs()
    }

    async runLifecycleAction<T>(name: string, context: ClientToolContext, operation: () => Promise<T>): Promise<T> {
        return this.enqueueAction(context.sessionId, async () => {
            this.throwIfActionCancelled(context)
            runInAction(() => {
                this.activeAgentActivities.set(context.sessionId, { name, context })
            })
            try {
                const result = await operation()
                this.throwIfActionCancelled(context)
                return result
            } finally {
                runInAction(() => {
                    this.activeAgentActivities.delete(context.sessionId)
                })
            }
        })
    }

    async runAgentAction<T>(name: string, context: ClientToolContext, operation: () => Promise<T>, options: { allowWhilePaused?: boolean } = {}): Promise<T> {
        const actionGeneration = this.generation
        const execute = async (): Promise<T> => {
            this.throwIfActionCancelled(context)
            if (actionGeneration !== this.generation) throw new Error("browser_action_cancelled")
            if (!embeddedBrowserSurfaceStore.isRunning) throw new Error("browser_start_required")
            if (this.isAgentPaused(context) && options.allowWhilePaused !== true) throw new Error("browser_model_control_paused")
            runInAction(() => {
                this.activeAgentActivities.set(context.sessionId, { name, context })
            })
            try {
                const result = await operation()
                this.throwIfActionCancelled(context)
                if (actionGeneration !== this.generation && this.agentStoppedGeneration !== actionGeneration) throw new Error("browser_action_cancelled")
                return result
            } catch (error) {
                if (error instanceof Error && error.message === "browser_sensitive_control") {
                    runInAction(() => this.requireManualAction(context))
                }
                throw error
            } finally {
                runInAction(() => {
                    this.activeAgentActivities.delete(context.sessionId)
                })
            }
        }
        return this.enqueueAction(context.sessionId, execute)
    }

    handleShortcut(tabId: string, command: EmbeddedBrowserShortcut): void {
        const sourceTab = this.tab(tabId)
        if (!sourceTab) return
        const sourceWasVisible = this.activeTabId === sourceTab.id
        if (command.type === "new-tab") {
            try {
                const tab = this.createTab("about:blank", {
                    ownerSessionId: sourceTab.ownerSessionId,
                    activate: sourceWasVisible,
                })
                if (sourceTab.ownerSessionId) this.agentActiveTabIds.set(sourceTab.ownerSessionId, tab.id)
                if (sourceWasVisible) this.addressFocusGeneration += 1
            } catch {
                this.setNotice("Browser can keep up to eight tabs open.")
            }
            return
        }
        if (command.type === "close-tab") {
            this.closeTab(tabId)
            return
        }
        if (command.type === "focus-address") {
            if (!sourceWasVisible) return
            this.addressFocusGeneration += 1
            return
        }
        if (command.type === "next-tab" || command.type === "previous-tab") {
            if (!sourceWasVisible) return
            const currentIndex = this.tabs.findIndex((tab) => tab.id === tabId)
            const offset = command.type === "next-tab" ? 1 : -1
            const target = this.tabs[(currentIndex + offset + this.tabs.length) % this.tabs.length]
            if (target) this.selectTab(target.id)
            return
        }
        if (command.type === "select-tab") {
            if (!sourceWasVisible) return
            const target = command.index === 8 ? this.tabs[this.tabs.length - 1] : this.tabs[command.index]
            if (target) this.selectTab(target.id)
            return
        }
        void this.surface(tabId).then((surface) => {
            if (command.type === "reload") surface.reload()
            else if (command.type === "hard-reload") surface.reloadIgnoringCache()
            else if (command.type === "go-back" && sourceTab.canGoBack) surface.goBack()
            else if (command.type === "go-forward" && sourceTab.canGoForward) surface.goForward()
        })
    }

    private tab(tabId: string): EmbeddedBrowserTab | null {
        return this.tabs.find((candidate) => candidate.id === tabId) ?? null
    }

    private tabForGuest(guestId: number): EmbeddedBrowserTab | null {
        for (const tab of this.tabs) {
            if (tab.guestId() === guestId) return tab
        }
        return null
    }

    private syncActiveTabs(): void {
        for (const tab of this.tabs) {
            tab.setActive(tab.id === this.activeTabId)
            tab.setSurfaceInteractive(this.surfaceInteractive)
        }
    }

    private enqueueAction<T>(sessionId: string, execute: () => Promise<T>): Promise<T> {
        const previous = this.actionQueues.get(sessionId) ?? Promise.resolve()
        const result = previous.then(execute, execute)
        const tail = result.then(
            () => undefined,
            () => undefined
        )
        this.actionQueues.set(sessionId, tail)
        void tail.then(() => {
            if (this.actionQueues.get(sessionId) === tail) this.actionQueues.delete(sessionId)
        })
        return result
    }

    private clearScreenshots(tabId: string): void {
        for (const [id, observation] of this.screenshotObservations) {
            if (observation.tabId === tabId) this.screenshotObservations.delete(id)
        }
    }

    private throwIfActionCancelled(context: ClientToolContext): void {
        if (context.signal.aborted) throw new Error("browser_action_cancelled")
    }
}

export const embeddedBrowserStore = new EmbeddedBrowserStore()
